/**
 * A paper seen being written in this page has settled, and its row is out of view: say so,
 * over the existing .toast. Papers that settle while it is up share it; it closes itself
 * eight seconds after the last one, and is never modal.
 */
import { useEffect } from "react";

/** Each paper by id and its label, "Paper 3". */
export type Settled = {
  ready: { id: string; label: string }[];
  failed: { id: string; label: string }[];
};

type Props = {
  settled: Settled;
  onOpen: (paperId: string) => void;
  onShowPapers: () => void;
  onDismiss: () => void;
};

export const TOAST_MS = 8000;

export function toastText({ ready, failed }: Settled): string {
  const r = ready.length;
  const f = failed.length;
  if (f === 0) return r === 1 ? `${ready[0]!.label} is ready` : `${r} papers are ready`;
  if (r === 0)
    return f === 1 ? `${failed[0]!.label} couldn't be written` : `${f} papers couldn't be written`;
  return `${r} ready, ${f} couldn't be written`;
}

export function ReadyToast({ settled, onOpen, onShowPapers, onDismiss }: Props) {
  const count = settled.ready.length + settled.failed.length;
  useEffect(() => {
    const t = setTimeout(onDismiss, TOAST_MS);
    return () => clearTimeout(t);
    // A paper joining the toast starts its eight seconds again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);
  const one = settled.failed.length === 0 && settled.ready.length === 1;
  return (
    <div className="toast" role="status">
      <span>{toastText(settled)}</span>
      {one ? (
        <button
          type="button"
          onClick={() => {
            onOpen(settled.ready[0]!.id);
            onDismiss();
          }}
        >
          Open
        </button>
      ) : (
        <button
          type="button"
          onClick={() => {
            onShowPapers();
            onDismiss();
          }}
        >
          {settled.failed.length > 0 && settled.ready.length === 0 ? "See Papers" : "Open Papers"}
        </button>
      )}
      <button type="button" aria-label="Dismiss" onClick={onDismiss}>
        ✕
      </button>
    </div>
  );
}
